(() => {
  const OUTPUT_SIZE = 1080;
  const MAX_FILE_SIZE = 10 * 1024 * 1024;
  const ALLOWED_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp']);
  const MIN_FONT_SIZE = 8;
  const CANVAS_FONT_FAMILY = '"Startup Revolution Poppins"';

  class SrImageGenerator extends HTMLElement {
    connectedCallback() {
      if (this.connected) return;
      this.connected = true;
      this.abortController = new AbortController();
      this.renderToken = 0;
      this.uploadLoadToken = 0;
      this.renderedToken = 0;
      this.renderFrame = null;
      this.uploadImage = null;
      this.uploadObjectUrl = '';
      this.backgroundPromises = new Map();
      this.fontPromises = new Map();

      this.canvas = this.querySelector('[data-generator-canvas]');
      this.context = this.canvas?.getContext('2d');
      this.form = this.querySelector('[data-generator-form]');
      this.audienceInputs = [...this.querySelectorAll('[data-audience-input]')];
      this.templateInputs = [...this.querySelectorAll('[data-template-input]')];
      this.templateOptions = [...this.querySelectorAll('[data-template-option]')];
      this.nameInput = this.querySelector('[data-name-input]');
      this.nameField = this.querySelector('[data-name-field]');
      this.nameLabel = this.querySelector('[data-name-label]');
      this.positionInput = this.querySelector('[data-position-input]');
      this.positionField = this.querySelector('[data-position-field]');
      this.uploadInput = this.querySelector('[data-upload-input]');
      this.downloadButton = this.querySelector('[data-download-button]');
      this.downloadHelp = this.querySelector('[data-download-help]');
      this.status = this.querySelector('[data-status]');
      this.loadingIndicator = this.querySelector('[data-preview-loading]');
      this.emptyTemplates = this.querySelector('[data-empty-templates]');

      if (!this.context || !this.form) return;

      try {
        const configElement = this.querySelector('[data-generator-config]');
        this.config = JSON.parse(configElement?.textContent || '{"templates":[]}');
      } catch (error) {
        this.config = { templates: [] };
        this.setStatus('The template configuration could not be read. Please refresh the page.', 'error');
      }

      this.templates = Array.isArray(this.config.templates) ? this.config.templates : [];
      this.templateById = new Map(this.templates.map((template) => [template.id, template]));
      this.bindEvents();
      this.initializeSelection();
      this.updateTemplateVisibility();
      this.updateFieldVisibility();
      this.scheduleRender();
    }

    disconnectedCallback() {
      this.connected = false;
      this.renderToken += 1;
      this.uploadLoadToken += 1;
      this.abortController?.abort();
      if (this.renderFrame !== null) cancelAnimationFrame(this.renderFrame);
      this.renderFrame = null;
      this.revokeUploadUrl();
    }

    bindEvents() {
      const signal = this.abortController.signal;

      this.audienceInputs.forEach((input) => {
        input.addEventListener('change', () => this.handleAudienceChange(input.value), { signal });
      });

      this.templateInputs.forEach((input) => {
        input.addEventListener('change', () => {
          if (!input.checked) return;
          this.selectedTemplateId = input.value;
          this.updateFieldVisibility();
          this.scheduleRender();
        }, { signal });
      });

      this.nameInput?.addEventListener('input', () => this.scheduleRender(), { signal });
      this.positionInput?.addEventListener('input', () => this.scheduleRender(), { signal });
      this.uploadInput?.addEventListener('change', () => this.handleUpload(), { signal });
      this.downloadButton?.addEventListener('click', () => this.download(), { signal });

      this.addEventListener('shopify:block:select', (event) => {
        const option = event.target.closest?.('[data-template-option]');
        if (!option) return;
        if (option.dataset.complete === 'true' && option.dataset.enabled === 'true') {
          this.selectTemplate(option.dataset.templateId);
          return;
        }
        if (document.documentElement.classList.contains('shopify-design-mode')) {
          this.selectedAudience = option.dataset.audience;
          this.selectedTemplateId = '';
          this.syncCheckedInputs();
          this.updateTemplateVisibility();
          this.updateFieldVisibility();
          this.scheduleRender();
        }
      }, { signal });
    }

    get usableTemplates() {
      return this.templates.filter((template) => (
        template.enabled && template.backgroundUrl && ['company', 'individual'].includes(template.audience)
      ));
    }

    initializeSelection() {
      const firstTemplate = this.usableTemplates[0];
      if (!firstTemplate) {
        const firstConfiguredAudience = this.templates.find((template) => (
          ['company', 'individual'].includes(template.audience)
        ))?.audience;
        this.selectedAudience = firstConfiguredAudience || 'company';
        this.selectedTemplateId = '';
        this.audienceInputs.forEach((input) => {
          input.checked = input.value === this.selectedAudience;
          input.disabled = true;
        });
        this.updateDownloadState();
        return;
      }

      this.audienceInputs.forEach((input) => {
        const hasTemplates = this.usableTemplates.some((template) => template.audience === input.value);
        input.disabled = !hasTemplates;
      });

      this.selectedAudience = firstTemplate.audience;
      this.selectedTemplateId = firstTemplate.id;
      this.syncCheckedInputs();
    }

    handleAudienceChange(audience) {
      if (audience === this.selectedAudience) return;
      const firstTemplate = this.usableTemplates.find((template) => template.audience === audience);
      if (!firstTemplate) return;
      this.selectedAudience = audience;
      this.selectedTemplateId = firstTemplate.id;
      this.syncCheckedInputs();
      this.updateTemplateVisibility();
      this.updateFieldVisibility();
      this.scheduleRender();
    }

    selectTemplate(templateId) {
      const template = this.templateById.get(templateId);
      if (!template?.enabled || !template.backgroundUrl) return;
      this.selectedAudience = template.audience;
      this.selectedTemplateId = template.id;
      this.syncCheckedInputs();
      this.updateTemplateVisibility();
      this.updateFieldVisibility();
      this.scheduleRender();
    }

    syncCheckedInputs() {
      this.audienceInputs.forEach((input) => {
        input.checked = input.value === this.selectedAudience;
      });
      this.templateInputs.forEach((input) => {
        input.checked = input.value === this.selectedTemplateId;
      });
    }

    updateTemplateVisibility() {
      const designMode = document.documentElement.classList.contains('shopify-design-mode');
      let visibleUsableCount = 0;

      this.templateOptions.forEach((option) => {
        const matchesAudience = option.dataset.audience === this.selectedAudience;
        const enabled = option.dataset.enabled === 'true';
        const complete = option.dataset.complete === 'true';
        const shouldShow = matchesAudience && ((enabled && complete) || designMode);
        option.hidden = !shouldShow;
        if (shouldShow && enabled && complete) visibleUsableCount += 1;
      });

      if (this.emptyTemplates) {
        this.emptyTemplates.classList.toggle('is-hidden', visibleUsableCount > 0);
      }
    }

    updateFieldVisibility() {
      const template = this.templateById.get(this.selectedTemplateId);
      const usesPosition = Boolean(template?.position?.enabled);

      if (this.positionField) this.positionField.hidden = !usesPosition;
      if (this.positionInput) {
        this.positionInput.disabled = !usesPosition;
        this.positionInput.required = usesPosition;
      }

      this.updateNameFieldWording();
    }

    updateNameFieldWording() {
      if (!this.nameField) return;
      const isCompany = this.selectedAudience === 'company';
      const { dataset } = this.nameField;
      const label = isCompany ? dataset.labelCompany : dataset.labelIndividual;
      const placeholder = isCompany ? dataset.placeholderCompany : dataset.placeholderIndividual;

      if (this.nameLabel && label) this.nameLabel.textContent = label;
      if (this.nameInput) {
        if (placeholder) this.nameInput.placeholder = placeholder;
        this.nameInput.autocomplete = isCompany ? 'organization' : 'name';
      }
    }

    async handleUpload() {
      const file = this.uploadInput?.files?.[0];
      if (!file) return;

      if (!ALLOWED_TYPES.has(file.type)) {
        this.uploadInput.value = '';
        this.setStatus('Unsupported file format. Choose a PNG, JPEG, or WebP image.', 'error');
        this.updateDownloadState();
        return;
      }

      if (file.size > MAX_FILE_SIZE) {
        this.uploadInput.value = '';
        this.setStatus('That image is over 10 MB. Choose a smaller file.', 'error');
        this.updateDownloadState();
        return;
      }

      const loadToken = ++this.uploadLoadToken;
      const objectUrl = URL.createObjectURL(file);
      const image = new Image();
      image.src = objectUrl;
      this.setStatus('Loading your image…');

      try {
        await this.decodeImage(image);
        if (!this.connected || loadToken !== this.uploadLoadToken) {
          URL.revokeObjectURL(objectUrl);
          return;
        }

        this.revokeUploadUrl();
        this.uploadImage = image;
        this.uploadObjectUrl = objectUrl;
        this.setStatus('Image ready.', 'success');
        this.scheduleRender();
      } catch (error) {
        URL.revokeObjectURL(objectUrl);
        if (loadToken !== this.uploadLoadToken) return;
        this.uploadInput.value = '';
        this.setStatus('This image could not be opened. Try exporting it again as PNG, JPEG, or WebP.', 'error');
        this.updateDownloadState();
      }
    }

    decodeImage(image) {
      if (typeof image.decode === 'function') return image.decode();
      return new Promise((resolve, reject) => {
        image.addEventListener('load', resolve, { once: true });
        image.addEventListener('error', reject, { once: true });
      });
    }

    revokeUploadUrl() {
      if (!this.uploadObjectUrl) return;
      URL.revokeObjectURL(this.uploadObjectUrl);
      this.uploadObjectUrl = '';
    }

    scheduleRender() {
      if (this.renderFrame !== null) cancelAnimationFrame(this.renderFrame);
      this.renderedToken = 0;
      this.updateDownloadState();
      this.renderFrame = requestAnimationFrame(() => {
        this.renderFrame = null;
        this.render();
      });
    }

    async render() {
      const token = ++this.renderToken;
      const template = this.templateById.get(this.selectedTemplateId);
      this.renderedToken = 0;
      this.setLoading(Boolean(template));
      this.updateDownloadState();

      if (!template?.enabled || !template.backgroundUrl) {
        this.context.clearRect(0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
        this.setLoading(false);
        this.setStatus('No complete template is available yet.', 'error');
        return;
      }

      try {
        const fontWeights = new Set();
        if (this.normalizedValue(this.nameInput?.value)) fontWeights.add(template.name.weight);
        if (template.position.enabled && this.normalizedValue(this.positionInput?.value)) {
          fontWeights.add(template.position.weight);
        }

        const [backgroundImage] = await Promise.all([
          this.loadBackground(template.backgroundUrl),
          ...[...fontWeights].map((weight) => this.loadFont(weight))
        ]);

        if (!this.connected || token !== this.renderToken) return;

        this.context.clearRect(0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
        this.context.drawImage(backgroundImage, 0, 0, OUTPUT_SIZE, OUTPUT_SIZE);

        if (this.uploadImage) this.drawUploadedImage(template.upload);

        const warnings = [];
        const nameWarning = this.drawFittedText(this.nameInput?.value, template.name);
        const positionWarning = template.position.enabled
          ? this.drawFittedText(this.positionInput?.value, template.position)
          : false;
        if (nameWarning) warnings.push('name');
        if (positionWarning) warnings.push('position');

        this.renderedToken = token;
        this.setLoading(false);
        this.updateDownloadState();

        if (warnings.length) {
          this.setStatus(`The ${warnings.join(' and ')} text is very small. Shorten it if readability is important.`, 'warning');
        } else if (this.isComplete()) {
          this.setStatus('Your image is ready to download.', 'success');
        }
      } catch (error) {
        if (!this.connected || token !== this.renderToken) return;
        this.setLoading(false);
        this.renderedToken = 0;
        const message = error?.code === 'font'
          ? 'Poppins could not be loaded. Check your connection and try again.'
          : 'The template background could not be loaded. Try again or choose another template.';
        this.setStatus(message, 'error');
        this.updateDownloadState();
      }
    }

    loadBackground(src) {
      if (this.backgroundPromises.has(src)) return this.backgroundPromises.get(src);

      const promise = new Promise((resolve, reject) => {
        const image = new Image();
        image.crossOrigin = 'anonymous';
        image.addEventListener('load', () => resolve(image), { once: true });
        image.addEventListener('error', reject, { once: true });
        image.src = src;
        if (image.complete && image.naturalWidth) resolve(image);
      }).catch((error) => {
        this.backgroundPromises.delete(src);
        throw error;
      });

      this.backgroundPromises.set(src, promise);
      return promise;
    }

    loadFont(weight) {
      const normalizedWeight = String(weight || '700');
      if (this.fontPromises.has(normalizedWeight)) return this.fontPromises.get(normalizedWeight);

      const promise = (async () => {
        if (!document.fonts) {
          const error = new Error('Font Loading API unavailable');
          error.code = 'font';
          throw error;
        }
        const descriptor = `${normalizedWeight} 200px ${CANVAS_FONT_FAMILY}`;
        const loadedFaces = await document.fonts.load(descriptor, 'Startup Revolution');
        await document.fonts.ready;
        if (!loadedFaces.length || !document.fonts.check(descriptor, 'Startup Revolution')) {
          const error = new Error('Poppins unavailable');
          error.code = 'font';
          throw error;
        }
      })().catch((error) => {
        this.fontPromises.delete(normalizedWeight);
        error.code = 'font';
        throw error;
      });

      this.fontPromises.set(normalizedWeight, promise);
      return promise;
    }

    drawUploadedImage(settings) {
      const rect = this.clampedRect(settings);
      const image = this.uploadImage;
      if (!image?.naturalWidth || !image.naturalHeight || rect.width <= 0 || rect.height <= 0) return;

      this.context.save();
      this.createClipPath(rect, settings.shape, settings.cornerRadius);
      this.context.clip();

      const scale = settings.fit === 'contain'
        ? Math.min(rect.width / image.naturalWidth, rect.height / image.naturalHeight)
        : Math.max(rect.width / image.naturalWidth, rect.height / image.naturalHeight);
      const width = image.naturalWidth * scale;
      const height = image.naturalHeight * scale;
      const x = rect.x + (rect.width - width) / 2;
      const y = rect.y + (rect.height - height) / 2;
      this.context.drawImage(image, x, y, width, height);
      this.context.restore();
    }

    createClipPath(rect, shape, radiusPercent) {
      const context = this.context;
      context.beginPath();

      if (shape === 'circle') {
        const radius = Math.min(rect.width, rect.height) / 2;
        context.arc(rect.x + rect.width / 2, rect.y + rect.height / 2, radius, 0, Math.PI * 2);
        context.closePath();
        return;
      }

      if (shape === 'rounded') {
        const radius = Math.min(
          Math.min(rect.width, rect.height) * this.clampNumber(radiusPercent, 0, 50) / 100,
          rect.width / 2,
          rect.height / 2
        );
        context.moveTo(rect.x + radius, rect.y);
        context.lineTo(rect.x + rect.width - radius, rect.y);
        context.quadraticCurveTo(rect.x + rect.width, rect.y, rect.x + rect.width, rect.y + radius);
        context.lineTo(rect.x + rect.width, rect.y + rect.height - radius);
        context.quadraticCurveTo(
          rect.x + rect.width,
          rect.y + rect.height,
          rect.x + rect.width - radius,
          rect.y + rect.height
        );
        context.lineTo(rect.x + radius, rect.y + rect.height);
        context.quadraticCurveTo(rect.x, rect.y + rect.height, rect.x, rect.y + rect.height - radius);
        context.lineTo(rect.x, rect.y + radius);
        context.quadraticCurveTo(rect.x, rect.y, rect.x + radius, rect.y);
        context.closePath();
        return;
      }

      context.rect(rect.x, rect.y, rect.width, rect.height);
    }

    clampedRect(settings) {
      const x = Math.min(OUTPUT_SIZE - 1, this.clampNumber(settings.x, 0, 100) / 100 * OUTPUT_SIZE);
      const y = Math.min(OUTPUT_SIZE - 1, this.clampNumber(settings.y, 0, 100) / 100 * OUTPUT_SIZE);
      const requestedWidth = this.clampNumber(settings.width, 1, 100) / 100 * OUTPUT_SIZE;
      const requestedHeight = this.clampNumber(settings.height, 1, 100) / 100 * OUTPUT_SIZE;
      return {
        x,
        y,
        width: Math.max(1, Math.min(requestedWidth, OUTPUT_SIZE - x)),
        height: Math.max(1, Math.min(requestedHeight, OUTPUT_SIZE - y))
      };
    }

    drawFittedText(value, settings) {
      const text = this.normalizedValue(value);
      if (!text) return false;

      const x = Math.min(OUTPUT_SIZE - 1, this.clampNumber(settings.x, 0, 100) / 100 * OUTPUT_SIZE);
      const y = Math.min(OUTPUT_SIZE - 1, this.clampNumber(settings.y, 0, 100) / 100 * OUTPUT_SIZE);
      const width = Math.max(
        1,
        Math.min(this.clampNumber(settings.width, 1, 100) / 100 * OUTPUT_SIZE, OUTPUT_SIZE - x)
      );
      const maxFontSize = this.clampNumber(settings.fontSize, MIN_FONT_SIZE, 200);
      const weight = String(settings.weight || '700');
      let low = MIN_FONT_SIZE;
      let high = maxFontSize;

      this.context.font = `${weight} ${high}px ${CANVAS_FONT_FAMILY}`;
      const maxLines = Math.round(this.clampNumber(settings.maxLines, 1, 3));
      let layout = this.layoutText(text, width, maxLines);
      if (!layout.fits) {
        for (let iteration = 0; iteration < 14; iteration += 1) {
          const middle = (low + high) / 2;
          this.context.font = `${weight} ${middle}px ${CANVAS_FONT_FAMILY}`;
          if (this.layoutText(text, width, maxLines).fits) {
            low = middle;
          } else {
            high = middle;
          }
        }
        high = low;
      }

      const fontSize = high;
      this.context.font = `${weight} ${fontSize}px ${CANVAS_FONT_FAMILY}`;
      layout = this.layoutText(text, width, maxLines);
      const metrics = this.context.measureText('Mg');
      const ascent = metrics.actualBoundingBoxAscent || fontSize * 0.8;
      const descent = metrics.actualBoundingBoxDescent || fontSize * 0.2;
      const lineHeight = fontSize * this.clampNumber(settings.lineHeight, 80, 200) / 100;
      const firstBaseline = y + (lineHeight - ascent - descent) / 2 + ascent;

      this.context.fillStyle = settings.color || '#ffffff';
      this.context.textAlign = ['left', 'center', 'right'].includes(settings.alignment)
        ? settings.alignment
        : 'left';
      this.context.textBaseline = 'alphabetic';

      const alignedX = this.context.textAlign === 'center'
        ? x + width / 2
        : this.context.textAlign === 'right'
          ? x + width
          : x;
      layout.lines.forEach((line, index) => {
        this.context.fillText(line, alignedX, firstBaseline + index * lineHeight);
      });

      return !layout.fits || fontSize <= MIN_FONT_SIZE + 0.1;
    }

    layoutText(text, width, maxLines) {
      const words = text.split(' ');
      const lines = [];
      let currentLine = '';

      words.forEach((word) => {
        const candidate = currentLine ? `${currentLine} ${word}` : word;
        if (!currentLine || this.context.measureText(candidate).width <= width) {
          currentLine = candidate;
          return;
        }

        lines.push(currentLine);
        currentLine = word;
      });

      if (currentLine) lines.push(currentLine);

      return {
        lines: lines.slice(0, maxLines),
        fits: lines.length <= maxLines && lines.every((line) => this.context.measureText(line).width <= width)
      };
    }

    normalizedValue(value) {
      return String(value || '').replace(/\s+/g, ' ').trim();
    }

    clampNumber(value, minimum, maximum) {
      const number = Number(value);
      return Math.min(maximum, Math.max(minimum, Number.isFinite(number) ? number : minimum));
    }

    isComplete() {
      const template = this.templateById.get(this.selectedTemplateId);
      const hasRequiredPosition = !template?.position?.enabled || this.normalizedValue(this.positionInput?.value);
      return Boolean(
        template?.backgroundUrl
        && this.uploadImage
        && this.normalizedValue(this.nameInput?.value)
        && hasRequiredPosition
      );
    }

    updateDownloadState() {
      const missing = [];
      if (!this.templateById.get(this.selectedTemplateId)?.backgroundUrl) missing.push('choose a template');
      if (!this.uploadImage) missing.push('upload an image');
      if (!this.normalizedValue(this.nameInput?.value)) {
        missing.push(this.selectedAudience === 'company' ? 'enter a company' : 'enter a name');
      }
      const template = this.templateById.get(this.selectedTemplateId);
      if (template?.position?.enabled && !this.normalizedValue(this.positionInput?.value)) {
        missing.push('enter a position');
      }

      const renderReady = this.renderedToken > 0 && this.renderedToken === this.renderToken;
      this.downloadButton.disabled = missing.length > 0 || !renderReady;

      if (missing.length) {
        this.downloadHelp.textContent = `To download: ${missing.join(', ')}.`;
      } else if (!renderReady) {
        this.downloadHelp.textContent = 'Wait for the latest preview to finish.';
      } else {
        this.downloadHelp.textContent = 'Downloads a 1080 × 1080 PNG.';
      }
    }

    setLoading(isLoading) {
      if (!this.loadingIndicator) return;
      this.loadingIndicator.hidden = !isLoading;
    }

    setStatus(message, state = '') {
      if (!this.status) return;
      this.status.textContent = message;
      if (state) {
        this.status.dataset.state = state;
      } else {
        delete this.status.dataset.state;
      }
    }

    async download() {
      if (this.downloadButton.disabled || !this.isComplete()) {
        this.setStatus('Complete all fields and wait for the preview before downloading.', 'error');
        return;
      }

      const template = this.templateById.get(this.selectedTemplateId);
      const expectedToken = this.renderToken;
      this.downloadButton.disabled = true;
      this.setStatus('Preparing your download…');

      try {
        const blob = await new Promise((resolve, reject) => {
          this.canvas.toBlob((result) => {
            if (result) resolve(result);
            else reject(new Error('Canvas export failed'));
          }, 'image/png');
        });

        if (expectedToken !== this.renderToken || this.renderedToken !== this.renderToken) {
          throw new Error('Preview changed during export');
        }

        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        const templateSlug = this.slugify(template.label) || 'post';
        const nameSlug = this.slugify(this.nameInput.value) || 'attendee';
        anchor.href = url;
        anchor.download = `startup-revolution-${templateSlug}-${nameSlug}.png`;
        anchor.hidden = true;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), 1000);
        this.setStatus('Your image was downloaded.', 'success');
      } catch (error) {
        this.setStatus('The image could not be downloaded. Please try again.', 'error');
      } finally {
        this.updateDownloadState();
      }
    }

    slugify(value) {
      return String(value || '')
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 50);
    }
  }

  if (!customElements.get('sr-image-generator')) {
    customElements.define('sr-image-generator', SrImageGenerator);
  }
})();
